<template>
  <div class="w-100 my-5">
    <div v-if="transactionList">
      <div class="row mx-auto">
        <div class="col-sm m-2 box-white d-flex flex-column">
          <span class="fa fa-euro-sign font-15">&nbsp;&nbsp;&nbsp;{{ $t('total_balance') }}</span>
          <span class="mt-3 font-bold font-50">{{ getPriceFormat(totalBalance) }}</span>
        </div>
        <div class="col-sm m-2 box-white d-flex flex-column">
          <span class="fa fa-euro-sign font-15 color-black">&nbsp;&nbsp;&nbsp;{{ $t('not_paid') }}</span>
          <span class="mt-3 font-bold font-50 color-black">{{ getPriceFormat(notPaid) }}</span>
        </div>
        <div class="col-sm m-2 box-white d-flex flex-column">
          <span class="fa fa-euro-sign font-15">&nbsp;&nbsp;&nbsp;{{ $t('already_paid') }}</span>
          <span class="mt-3 font-bold font-50">{{ getPriceFormat(alreadyPaid) }}</span>
        </div>
      </div>
      <div class="row mx-auto">
        <div class="col-sm m-2 box-white row">
          <div class="col-sm my-1 vertical-center" v-if="eventInfo">
            <img :width="60" :height="40" :src="eventInfo.photoUrl" />
            <span class="ml-2 font-15 font-bold">{{ eventInfo.name }}</span>
            <span class="ml-5 font-bold font-15 alpha-07">{{ $t('refund') }}:</span>
            <span class="mx-5 font-bold font-20">{{ items.length }}</span>
          </div>
          <div class="col-sm my-1 vertical-center" v-else>
            <span class="font-bold font-15 alpha-07">{{ $t('refund') }}:</span>
            <span class="mx-5 font-bold font-20">{{ statsMap ? statsMap['transaction'].refunds : 0 }}</span>
          </div>
          <div :class="`col-sm my-1 vertical-center ${isMobile ? '' : 'justify-content-end'}`">
            <input class="w-75" v-model="keyword" />
            <v-btn class="ml-2" icon small @click="keyword = ''" :disabled="!keyword">
              <i class="flaticon-circle"></i>
            </v-btn>
          </div>
        </div>
      </div>
      <div class="row mx-auto" v-for="(item, index) in items" :key="(item, index)">
        <div class="col-sm m-2 p-0 box-white row">
          <div class="col-sm-4 d-flex flex-column">
            <div class="my-1 vertical-center">
              <img class="circle-image-30" :src="item.organizerPhotoUrl" />
              <span class="ml-2 ellipsis font-12">{{ item.organizerName }}</span>
            </div>
            <span class="my-1 ellipsis font-bold font-12">{{ item.eventName }}</span>
            <div class="mt-2 d-flex justify-content-between" v-for="(detail, index2) in item.details" :key="(detail, index2)">
              <span class="ellipsis font-12">{{ detail.ticketName }}</span>
              <span class="ml-2 font-12">{{ `${getPriceFormat(detail.price)} * ${detail.quantity} = ${getPriceFormat(detail.price * detail.quantity)}` }}</span>
            </div>
          </div>
          <div class="vertical-center">
            <div class="my-5 div-divider-h" v-if="isMobile" />
            <div class="mx-3 div-divider-v" v-else />
          </div>
          <div class="col-sm d-flex flex-column">
            <div class="my-1 vertical-center">
              <div class="d-flex justify-content-center">
                <img class="circle-image-30" :src="item.buyerPhotoUrl" />
              </div>
              <div class="ml-2 d-flex flex-column">
                <span class="ellipsis font-12">{{ item.buyerName }}</span>
                <span class="ellipsis font-12">{{ item.buyerEmail }}</span>
              </div>
            </div>
            <div class="mt-5 d-flex justify-content-between">
              <span class="font-12">{{ $t('ticket_price') }}</span>
              <span class="ml-2 font-12">{{ getPriceFormat(item.ticketPrice) }}</span>
            </div>
            <div class="mt-2 d-flex justify-content-between">
              <span class="font-12">{{ $t('tax_price') }}</span>
              <span class="ml-2 font-12">{{ getPriceFormat(item.taxPrice) }}</span>
            </div>
            <div class="mt-2 d-flex justify-content-between">
              <span class="font-12">{{ $t('service_charge') }}</span>
              <span class="ml-2 font-12">{{ getPriceFormat(item.serviceCharge) }}</span>
            </div>
            <div class="mt-2 d-flex justify-content-between">
              <span class="font-15">{{ $t('total_price') }}</span>
              <span class="ml-2 font-bold font-15">{{ getPriceFormat(item.totalPrice) }}</span>
            </div>
          </div>
          <div class="vertical-center">
            <div class="my-5 div-divider-h" v-if="isMobile" />
            <div class="mx-3 div-divider-v" v-else />
          </div>
          <div class="col-sm d-flex flex-column">
            <span class="mt-2 font-bold font-15 color-black" v-if="item.transactionId.startsWith(PAYMENT_TYPE_PAYPAL_SANDBOX) || item.transactionId.startsWith(PAYMENT_TYPE_STRIPE_TEST)">Sandbox</span>
            <div class="mt-2 d-flex vertical-center">
              <img height="30px" :src="item.transactionId.startsWith(PAYMENT_TYPE_PAYPAL) ? assets.paypal : assets.stripe" />
              <img class="ml-2" height="30px" :src="assets[item.paymentMethod]" v-if="item.paymentMethod && item.paymentMethod !== 'paypal'" />
              <span class="ml-auto font-12">{{ getDateStringFromTimestamp(item.createdAt) }}</span>
            </div>
            <div class="mt-2 d-flex justify-content-between">
              <span class="font-12">{{ $t('requested') }}</span>
              <span class="ml-2 font-bold font-12">{{ getDateStringFromTimestamp(item.refundRequestedAt) }}</span>
            </div>
            <div class="mt-5 d-flex justify-content-between" v-if="item.refundPaidAt">
              <span class="font-12">{{ $t('paid') }}</span>
              <span class="ml-2 font-12 font-bold">{{ getDateStringFromTimestamp(item.refundPaidAt) }}</span>
            </div>
            <div class="mt-5 d-flex justify-content-between" v-else-if="item.refundPendingAt">
              <span class="font-12 color-black">{{ $t('pending') }}</span>
              <span class="ml-2 font-12 font-bold color-black">{{ getDateStringFromTimestamp(item.refundPendingAt) }}</span>
            </div>
            <v-btn class="mt-5 ml-auto button-normal" small @click="doRefundTask(item.transactionId, item.ticketPrice)" v-else>
              <span>{{ $t('refund_now') }}</span>
            </v-btn>
          </div>
        </div>
      </div>
      <div class="py-40 center" v-if="(items.length === 0)">
        <span class="font-20 alpha-07">{{ $t('no_data_found') }}</span>
      </div>
    </div>
    <div class="py-40 center" v-else>
      <p>{{ $t('please_wait') }}</p>
      <b-spinner type="grow"></b-spinner>
    </div>
  </div>
</template>


<script>
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../main';
import { getPriceFormat, getDateStringFromTimestamp, showLoading } from '../../../functions';

import profile from '@/assets/image/profile.jpg';
import paypal from '@/assets/image/paypal.png';
import stripe from '@/assets/image/stripe.png';
import card from '@/assets/image/card.png';
import sepa from '@/assets/image/sepa.png';
import sepa_debit from '@/assets/image/sepa.png';
import sofort from '@/assets/image/sofort.png';
import klarna from '@/assets/image/klarna.png';
import giropay from '@/assets/image/giropay.png';

export default {
  name: 'RefundList',
  computed: {
    isMobile() {
      return this.$store.state.isMobile;
    },
    statsMap() {
      return this.$store.state.statsMap;
    },
    userList() {
      return this.$store.state.userList;
    },
    eventList() {
      return this.$store.state.eventList;
    },
    eventInfo() {
      if (this.$route.params.eventId) {
        return this.eventList ? this.eventList.find(element => element.eventId === this.$route.params.eventId) : null;
      } else {
        return null;
      }
    },
    transactionList() {
      return this.$store.state.transactionList;
    },
    params() {
      return this.$route.params;
    }
  },
  watch: {
    params() {
      if (this.keyword) {
        this.keyword = '';
      } else {
        this.refreshData();
      }
    },
    eventList() {
      this.refreshData();
    },
    userList() {
      this.refreshData();
    },
    transactionList() {
      this.refreshData();
    },
    keyword() {
      this.refreshData();
    }
  },
  data() {
    return {
      assets: {
        profile,
        paypal,
        stripe,
        card,
        sepa,
        sepa_debit,
        sofort,
        klarna,
        giropay
      },
      totalBalance: 0,
      notPaid: 0,
      alreadyPaid: 0,
      items: [],
      transactionId: '',
      amount: 0,
      keyword: ''
    }
  },
  mounted() {
    this.refreshData();
  },
  methods: {
    getPriceFormat,
    getDateStringFromTimestamp,
    refreshData() {
      if (this.transactionList && this.userList && this.eventList) {
        var totalBalance = 0;
        var notPaid = 0;
        var alreadyPaid = 0;
        const items = [];
        const keyword = this.keyword.toLowerCase();
        this.transactionList.filter(element => element.refundRequestedAt && (!this.$route.params.eventId || element.eventId === this.$route.params.eventId)).forEach(transactionInfo => {
          const buyerInfo = this.getUserInfo(transactionInfo.buyerId);
          const organizerInfo = this.getUserInfo(transactionInfo.organizerId);
          const eventInfo = this.getEventInfo(transactionInfo.eventId);
          if (buyerInfo && organizerInfo && eventInfo &&
            (eventInfo.name.toLowerCase().includes(keyword) || buyerInfo.userName.toLowerCase().includes(keyword) || organizerInfo.userName.toLowerCase().includes(keyword))) {
            const item = JSON.parse(JSON.stringify(transactionInfo));
            item['buyerName'] = buyerInfo.userName;
            item['buyerPhotoUrl'] = buyerInfo.photoUrl ? buyerInfo.photoUrl : this.assets.profile;
            item['buyerEmail'] = buyerInfo.email;
            item['organizerName'] = organizerInfo.userName;
            item['organizerPhotoUrl'] = organizerInfo.photoUrl ? organizerInfo.photoUrl : this.assets.profile;
            item['eventName'] = eventInfo.name;
            item['eventPhotoUrl'] = eventInfo.photoUrl;
            totalBalance += transactionInfo.totalPrice;
            if (transactionInfo.refundPaidAt) {
              alreadyPaid += transactionInfo.totalPrice;
            } else {
              notPaid += transactionInfo.totalPrice;
            }
            items.push(item);
          }
        });
        items.sort((a, b) => (a.refundRequestedAt.seconds < b.refundRequestedAt.seconds) ? 1 : -1);
        this.items = items;
        this.totalBalance = totalBalance;
        this.notPaid = notPaid;
        this.alreadyPaid = alreadyPaid;
      }
    },
    getUserInfo(userId) {
      return this.userList ? this.userList.find(element => element.userId === userId) : null;
    },
    getEventInfo(eventId) {
      return this.eventList ? this.eventList.find(element => element.eventId === eventId) : null;
    },
    doRefundTask(transactionId, amount) {
      if (this.transactionId || this.amount) {
        this.$toast.error('Unexpected error occurs.');
        return;
      }
      if (!confirm('Are you sure you want to refund this payment?')) {
        return;
      }
      const params = {
        functionName: 'doRefund',
        transactionId: transactionId,
        amount: amount
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          this.$toast.success('Refunded successfully.');
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    }
  }
};
</script>